import dynamic from 'next/dynamic';
import React, { memo, useState } from 'react';
import { useSelector } from 'react-redux';
import { Type_RefFuncKrpano } from '@components/Krpano/types';
import {
  selectSceneSelected,
  selectIsMatterportProject,
} from '@containers/Home/selectors';
import { useKrpano } from '@containers/Home/hook/useKrpano';
import { TourWrapper } from './styled';
import { I_Scene } from '../types';
import { TourRenderSceneKrpano } from './Tour.RenderSceneKrpano';
import { TourRenderSceneFlat360 } from './Tour.RenderSceneFlat360';

const Map = dynamic(() => import('@containers/Home/components/Map'));
const TourSound = dynamic(
  () => import('@containers/Home/components/Tour.Sound'),
);
const TourEvent = dynamic(
  () => import('@containers/Home/components/Tour.Event'),
);
const Floorplan = dynamic(
  () => import('@containers/Home/components/Floorplan'),
);
const ActionHotspot = dynamic(
  () => import('@containers/Home/components/ActionHotspot'),
);

const MatterportContainer = dynamic(
  () => import('@containers/Home/components/MatterportContainer'),
  { ssr: false },
);

export type PropChildrenComponents = {
  krpanoRef: Type_RefFuncKrpano | null;
};

export interface I_Tour {
  onNextScene: () => void;
  children?: (props: PropChildrenComponents) => React.ReactNode;
  onChangeScene: (scene: I_Scene) => void;
}

const Tour = ({ onChangeScene, onNextScene, children }: I_Tour) => {
  const sceneSelected = useSelector(selectSceneSelected);
  const isMatterportProject = useSelector(selectIsMatterportProject);

  const [isTourRendered, setIsTourRendered] = useState(false);
  const [krpanoRefState, setKrpanoRefState] =
    useState<Type_RefFuncKrpano | null>(null!);
  const { isShowIconMap, isShowIconFloorplan } = useKrpano({
    krpanoRef: null,
  });

  return (
    <TourWrapper>
      {isMatterportProject ? (
        <MatterportContainer />
      ) : sceneSelected?.object360?.imagePrefix ? (
        <TourRenderSceneFlat360 setIsTourRendered={setIsTourRendered} />
      ) : (
        <TourRenderSceneKrpano
          onChangeScene={onChangeScene}
          setIsTourRendered={setIsTourRendered}
          krpanoRefState={krpanoRefState}
          setKrpanoRefState={setKrpanoRefState}
        />
      )}

      {isTourRendered && (
        <>
          {children &&
            children({
              krpanoRef: krpanoRefState,
            })}

          {krpanoRefState && <TourSound krpanoRef={krpanoRefState} />}

          {krpanoRefState && (
            <TourEvent krpanoRef={krpanoRefState} onNextScene={onNextScene} />
          )}

          <ActionHotspot />
        </>
      )}

      {isShowIconMap && <Map onChangeScene={onChangeScene} />}
      {isShowIconFloorplan && <Floorplan onChangeScene={onChangeScene} />}
    </TourWrapper>
  );
};

export default memo(Tour);
